import { motion } from "framer-motion";
import { Quotes, GameController, Television, Users, Trophy } from "@phosphor-icons/react";

const chapters = [
    {
        num: "01",
        eyebrow: "Origin",
        icon: GameController,
        accent: "sky",
        ringClass: "ring-sky-400/50 hover:ring-sky-400/80",
        dotClass: "bg-sky-400",
        wash: "bg-gradient-to-br from-sky-500/[0.12] via-transparent to-transparent",
        title: "The name was an accident.",
        body: "Started as ooll56, a random throwaway from back when a game forced a username (yeah, it was Fortnite, only time you'll hear about it). Viewers and friends kept calling me 'ool.' Another streamer suggested ditching the numbers, and itz_ool was born.",
    },
    {
        num: "02",
        eyebrow: "Inspiration",
        icon: Television,
        accent: "violet",
        ringClass: "ring-violet-400/50 hover:ring-violet-400/80",
        dotClass: "bg-violet-400",
        wash: "bg-gradient-to-br from-violet-500/[0.12] via-transparent to-transparent",
        title: "NickEh30 lit the fuse.",
        body: "Watched NickEh30 stream and thought: 'I want to do that.' He was the role model, but somewhere along the way streaming stopped being inspiration and became the thing I actually cared about.",
    },
    {
        num: "03",
        eyebrow: "The community",
        icon: Users,
        accent: "rose",
        ringClass: "ring-rose-400/50 hover:ring-rose-400/80",
        dotClass: "bg-rose-400",
        wash: "bg-gradient-to-br from-rose-500/[0.12] via-transparent to-transparent",
        title: "Chat made it real.",
        body: "The streams are loud, weird, and unpredictable on purpose. One night chat completely melted down over a clip and that was the moment I knew this wasn't just a stream anymore, it was a community.",
    },
    {
        num: "04",
        eyebrow: "The mission",
        icon: Trophy,
        accent: "amber",
        ringClass: "ring-amber-400/50 hover:ring-amber-400/80",
        dotClass: "bg-amber-400",
        wash: "bg-gradient-to-br from-amber-500/[0.12] via-transparent to-transparent",
        title: "The win isn't numbers.",
        body: "Biggest achievement? Everything I've learned. The skills, the people, the chaos. Numbers are nice, but the dream is bigger: a real shot at the top 50.",
    },
];

export const About = () => {
    return (
        <section className="relative py-10 lg:py-14" data-testid="about-section">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
                {/* Hero pull-quote with gradient wash */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="relative overflow-hidden p-8 lg:p-10 mb-10 rounded-2xl border border-white/10 bg-obsidian-900/70"
                    data-testid="about-pullquote"
                >
                    {/* gradient wash */}
                    <div
                        aria-hidden
                        className="absolute inset-0 pointer-events-none opacity-90"
                        style={{
                            background:
                                "radial-gradient(900px 320px at 12% 12%, rgba(34,211,238,0.18), transparent 60%), radial-gradient(800px 320px at 90% 90%, rgba(196,160,255,0.18), transparent 65%), linear-gradient(135deg, rgba(34,211,238,0.06) 0%, rgba(196,160,255,0.06) 55%, rgba(251,113,133,0.05) 100%)",
                        }}
                    />
                    <div className="relative">
                        <Quotes size={22} weight="fill" className="text-brand-cyan/80 mb-4" />
                        <p className="font-display text-2xl sm:text-3xl text-white leading-snug max-w-3xl">
                            I'd rather lose count of the views than lose the people who actually show up.
                        </p>
                        <div className="mt-5 eyebrow text-cyan-300/80">— itz_ool · daily streams · 11 AM EST</div>
                    </div>
                </motion.div>

                {/* 2x2 chapter grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5">
                    {chapters.map((c, i) => {
                        const Icon = c.icon;
                        return (
                            <motion.div
                                key={c.num}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.5, delay: i * 0.06 }}
                                className={`relative overflow-hidden rounded-2xl border border-white/10 bg-obsidian-900/70 p-7 lg:p-8 ring-1 ${c.ringClass} transition`}
                                data-testid={`about-chapter-${i}`}
                            >
                                {/* huge background numeral */}
                                <span
                                    aria-hidden
                                    className="absolute -right-3 -top-6 font-display font-semibold text-white/[0.04] select-none pointer-events-none"
                                    style={{ fontSize: 160, lineHeight: 1 }}
                                >
                                    {c.num}
                                </span>
                                {/* accent wash */}
                                <div aria-hidden className={`absolute inset-0 pointer-events-none ${c.wash}`} />

                                <div className="relative">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                                            <Icon size={16} weight="bold" className="text-white/80" />
                                        </div>
                                        <span className={`h-1.5 w-1.5 rounded-full ${c.dotClass}`} />
                                        <span className="eyebrow text-white/55">{c.eyebrow}</span>
                                    </div>
                                    <h3 className="font-display text-xl sm:text-2xl text-white mb-3 leading-tight">{c.title}</h3>
                                    <p className="text-white/65 leading-relaxed text-[15px]">{c.body}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
